import type { ReportSection } from '../ReportSection.js'
import type { ProcessingProfile } from '../../../../../Model/Order/processingProfile.js'
import type { PerSampleRow } from '../summaryStats.js'
import { num, Title } from './primitives.js'

interface Column {
  label: (profile: ProcessingProfile) => string
  show: (profile: ProcessingProfile, rows: readonly PerSampleRow[]) => boolean
  cell: (row: PerSampleRow) => number | null
  format?: (value: number | null) => string
}

const percent = (value: number | null): string => (value == null ? 'n/a' : `${value.toFixed(1)} %`)

const COLUMNS: Column[] = [
  { label: (p) => (p.pairedEnd ? 'Read pairs' : 'Reads'), show: () => true, cell: (r) => r.raw },
  { label: () => 'Merged', show: (p) => p.mergeApplied, cell: (r) => r.merged },
  { label: () => 'Fwd primer', show: (p) => p.primerRemovalApplied, cell: (r) => r.forwardTrimmed },
  { label: () => 'Rev primer', show: (p) => p.primerRemovalApplied, cell: (r) => r.reverseTrimmed },
  { label: () => 'Quality filtered', show: () => true, cell: (r) => r.qualityFiltered },
  { label: (p) => (p.consensusApplied ? 'Consensus' : 'Unique'), show: () => true, cell: (r) => r.unique },
  {
    label: () => 'No BLAST hit',
    show: (_, rows) => rows.some((r) => r.unassignedPercent != null),
    cell: (r) => r.unassignedPercent,
    format: percent
  }
]

export const perSample: ReportSection = {
  id: 'perSample',
  applies: () => true,
  Render: ({ data: { profile, stats } }) => {
    const columns = COLUMNS.filter((c) => c.show(profile, stats.perSample))
    return (
      <section className="mt-4 p-4 break-before-page">
        <Title>Per-sample read tracking</Title>
        <table className="w-4/5 mx-auto text-xs border-collapse">
          <thead>
            <tr className="border-b border-gray-400 text-left">
              <th className="py-1 pr-2">Sample</th>
              <th className="py-1 pr-2">Pool</th>
              {columns.map((c) => (
                <th key={c.label(profile)} className="py-1 pr-2 text-right">
                  {c.label(profile)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {stats.perSample.map((row) => (
              <tr key={row.id} className="border-b border-gray-200 break-inside-avoid">
                <td className="py-1 pr-2">
                  {row.name}
                  {row.itemType === 'NEGATIVE_CONTROL' && <span className="ml-1 text-gray-500">(NC)</span>}
                </td>
                <td className="py-1 pr-2">{row.pool}</td>
                {columns.map((c) => (
                  <td key={c.label(profile)} className="py-1 pr-2 text-right">
                    {(c.format ?? num)(c.cell(row))}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </section>
    )
  }
}
